import React from 'react'
import { cn } from '@/utils'

type TrimFrameProps = {
  left: number
  top: number
  width: number
  height: number
  borderPx?: number
  borderColor?: string
  dashed?: boolean
  className?: string
  hideLeft?: boolean
  hideRight?: boolean
  hideTop?: boolean
  hideBottom?: boolean
  /** When set, stacks dashed trim in the canvas overlay (z≈10 in `CenterCanvas`) above shapes and bleed. */
  zIndex?: number
}

/**
 * Trim outline — matches Figma Page Editor Trim (node 2160:33518): 1px neutral stroke, short 1:1 dashes
 * via `stroke-dasharray` (not browser `border-style: dashed`, which varies by engine).
 */
const TrimFrame: React.FC<TrimFrameProps> = ({
  left,
  top,
  width,
  height,
  borderPx = 1,
  borderColor = 'var(--doc-trim-border)',
  dashed = true,
  className,
  hideLeft = false,
  hideRight = false,
  hideTop = false,
  hideBottom = false,
  zIndex,
}) => {
  if (width <= 0 || height <= 0) {
    return null
  }

  const inset = borderPx / 2
  const dashArray = dashed ? 'var(--doc-trim-stroke-dasharray)' : undefined
  const allSides = !hideLeft && !hideRight && !hideTop && !hideBottom

  return (
    <svg
      className={cn('absolute pointer-events-none overflow-visible', className)}
      width={width}
      height={height}
      style={{ left, top, ...(zIndex != null ? { zIndex } : {}) }}
      aria-hidden
    >
      {allSides ? (
        <rect
          x={inset}
          y={inset}
          width={Math.max(0, width - borderPx)}
          height={Math.max(0, height - borderPx)}
          fill="none"
          stroke={borderColor}
          strokeWidth={borderPx}
          strokeDasharray={dashArray}
        />
      ) : (
        <>
          {!hideTop && (
            <line
              x1={0}
              y1={inset}
              x2={width}
              y2={inset}
              stroke={borderColor}
              strokeWidth={borderPx}
              strokeDasharray={dashArray}
            />
          )}
          {!hideBottom && (
            <line
              x1={0}
              y1={height - inset}
              x2={width}
              y2={height - inset}
              stroke={borderColor}
              strokeWidth={borderPx}
              strokeDasharray={dashArray}
            />
          )}
          {!hideLeft && (
            <line
              x1={inset}
              y1={0}
              x2={inset}
              y2={height}
              stroke={borderColor}
              strokeWidth={borderPx}
              strokeDasharray={dashArray}
            />
          )}
          {!hideRight && (
            <line
              x1={width - inset}
              y1={0}
              x2={width - inset}
              y2={height}
              stroke={borderColor}
              strokeWidth={borderPx}
              strokeDasharray={dashArray}
            />
          )}
        </>
      )}
    </svg>
  )
}

export default TrimFrame
